import React from 'react'
import { Link } from 'react-router-dom'

import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import { EmptyState, List, Table, useMediaQueryContext } from '@fourthwall/components'
import { useSearchQuery } from '@modules/Dashboard/hooks/useSearchQuery'
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider'
import { routing } from '@utils/routing'

import { useFilterContributions } from '../../hooks/useFilterContributions'
import type { Order } from '../../types'
import { getOrderLink } from '../../utils/getOrderLink'
import { getOrdersTableHeaders } from '../../utils/getOrdersTableHeaders'
import { OrderListItem } from '../OrderListItem/OrderListItem'

type OrdersDataProps = {
  data: Order[]
  isLoading: boolean
}

export const OrdersData = ({ data, isLoading }: OrdersDataProps) => {
  const { hasPermission } = useDashboardPermissions()
  const media = useMediaQueryContext()
  const { searchQuery } = useSearchQuery()
  const { hasFilterParams } = useFilterContributions()
  const hasFilters = searchQuery || hasFilterParams
  const { timezoneAbbreviation } = useShopTime()

  const getEmptyStateSubtitle = () => {
    if (hasFilters) {
      return 'Adjust your filters or try different search term.'
    }

    return (
      <>
        Start selling <Link to={routing.products.all.self}>products</Link> or run{' '}
        <Link to={routing.promotions.self}>promotions</Link> to boost your sales!
      </>
    )
  }

  const emptyComponent = (
    <EmptyState
      variant={media.tablet ? 'primary' : 'secondary'}
      title={hasFilters ? 'No orders found' : 'You haven’t received any orders yet'}
      subtitle={getEmptyStateSubtitle()}
    />
  )

  if (!media.tablet) {
    return (
      <List
        data={data}
        isLoading={isLoading}
        emptyComponent={emptyComponent}
        renderItem={(order: Order) => <OrderListItem order={order} />}
        getItemLink={(order) => getOrderLink(order.id)}
      />
    )
  }

  return (
    <Table
      data={data}
      emptyComponent={emptyComponent}
      headers={getOrdersTableHeaders({
        hideProfitColumn: !hasPermission('contributions.profit'),
        timezoneAbbreviation,
      })}
      rowComponent={Link}
      isLoading={isLoading}
      getRowLink={({ id }) => getOrderLink(id)}
    />
  )
}
